import Color from 'color';
import { type KeyboardEvent, useRef, useState, useEffect, useId } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { FakeLink } from '@hivebrite/design-system/Link';
import { linkEffectForChildren } from '@hivebrite/design-system/Theme';
import { useIsMobileDevice } from '@hivebrite/react-hooks';
import { onEnterOrSpacePress } from '@hivebrite/utils';

import {
  useLiveFeedContext,
  useLandingContext,
} from 'hb-react/frontoffice/apps/LiveFeed/contexts';

import Content from './Content';

const MAX_HEIGHT_CONTENT = {
  desktop: 270,
  mobile: 250,
};

const ContentContainer = ({ content, ...otherProps }) => {
  const { id, type, originalType, measure, owner } = useLandingContext();
  const { isItemReadMoreExpanded, toggleItemReadMore } = useLiveFeedContext();
  const isMobileDevice = useIsMobileDevice();

  const a11yId = useId();
  const contentRef = useRef<HTMLDivElement>(null);
  const [hasReadMore, setHasReadMore] = useState(false);
  const [showMore, setShowMore] = useState(isItemReadMoreExpanded(id, type));
  const maxHeight = MAX_HEIGHT_CONTENT[isMobileDevice ? 'mobile' : 'desktop'];

  const handleToggleReadMore = (event?: KeyboardEvent) => {
    event?.preventDefault();
    setShowMore(!showMore);
    toggleItemReadMore(id, type, !showMore);
  };

  useEffect(() => {
    if (contentRef.current) {
      const { height } = contentRef.current.getBoundingClientRect();

      if (height > maxHeight) {
        setHasReadMore(true);
      } else {
        setHasReadMore(false);
      }
    }
  }, [maxHeight]);

  // Trigger measure every time we toggle "Read more/less"
  useEffect(() => {
    measure?.();
  }, [measure, showMore]);

  return (
    <Box flexDirection="column">
      <WrapperContent
        {...otherProps}
        $withGradient={hasReadMore && !showMore}
        data-testid="landing-content"
        ref={contentRef}
        style={
          hasReadMore
            ? {
                maxHeight: showMore ? '100%' : maxHeight,
              }
            : {}
        }
      >
        <Content
          a11yId={a11yId}
          content={content}
          id={id}
          hasMore={hasReadMore}
          showMore={showMore}
          type={originalType}
          user={owner}
        />
      </WrapperContent>
      {hasReadMore && (
        <Box justifyContent="center" mt={3}>
          <FakeLink
            aria-controls={a11yId}
            aria-expanded={showMore}
            css="user-select: none;"
            data-testid="read-more-button"
            display="inline-flex"
            font="body-semibold-asphalt"
            m={-1}
            onClick={handleToggleReadMore}
            onKeyDown={onEnterOrSpacePress(handleToggleReadMore)}
            p={1}
            role="button"
          >
            {!showMore ? __('Read more') : __('Read less')}
          </FakeLink>
        </Box>
      )}
    </Box>
  );
};

export default ContentContainer;

interface WrapperContentProps {
  mt?: number;
  $withGradient?: boolean;
}

const WrapperContent = styled(Box).attrs(({ mt }: WrapperContentProps) => ({
  className: 'ckeditor-content--reset',
  display: 'block',
  font: 'body-regular-asphalt',
  mt: mt ?? 6,
}))<WrapperContentProps>`
  ${linkEffectForChildren}

  overflow: hidden;
  position: relative;
  line-height: 1.6;
  word-wrap: break-word;

  > *:last-child {
    margin-bottom: 0;
  }

  ${(props) =>
    props.$withGradient &&
    `
    &:after {
      background: linear-gradient(to bottom, ${Color(
        props.theme.themeVariables.frameBackgroundColor,
      )
        .alpha(0)
        .string()} 0%, ${props.theme.themeVariables.frameBackgroundColor} 80%);
      bottom: 0;
      content: '';
      height: 65px;
      left: 0;
      position: absolute;
      right: 0;
    }
  `}
`;
